import { NPC_CATALOG } from '../data/videoNpcCatalog';

export type BillboardDirectionId =
  | 'front'
  | 'three-quarter'
  | 'profile'
  | 'back-three-quarter'
  | 'back'
  | 'opposite-profile';
export type BillboardReactionId = 'positive' | 'negative';
export type BillboardAssetId = BillboardDirectionId | BillboardReactionId;

export type BillboardCharacterId = string;

export interface BillboardAssetMeasurement {
  canvasWidth: number;
  canvasHeight: number;
  opaqueBodyTopPx: number;
  opaqueBodyBottomPx: number;
}

export interface BillboardView extends BillboardAssetMeasurement {
  id: BillboardDirectionId;
  yawRadians: number;
  yawDegrees: number;
  src: string;
}

export interface BillboardReaction extends BillboardAssetMeasurement {
  id: BillboardReactionId;
  src: string;
}

export interface BillboardAssets {
  views: BillboardView[];
  reactions: Record<BillboardReactionId, BillboardReaction>;
}

export type BillboardMeasurementSet = Record<BillboardAssetId, BillboardAssetMeasurement>;

// Keep the geometry helpers usable in isolation while sourcing all production
// measurements from the generated catalog.
export const NPC_BILLBOARD_MEASUREMENTS: Record<BillboardCharacterId, BillboardMeasurementSet> = Object.fromEntries(
  NPC_CATALOG.npcs.map((npc) => [
    npc.id,
    Object.fromEntries([
      ...npc.assets.views,
      ...Object.values(npc.assets.reactions),
    ].map((asset) => [asset.id, {
      canvasWidth: asset.canvasWidth,
      canvasHeight: asset.canvasHeight,
      opaqueBodyTopPx: asset.opaqueBodyTopPx,
      opaqueBodyBottomPx: asset.opaqueBodyBottomPx,
    }])) as BillboardMeasurementSet,
  ]),
) as Record<BillboardCharacterId, BillboardMeasurementSet>;

const BILLBOARD_DIRECTIONS: Array<Pick<BillboardView, 'id' | 'yawRadians' | 'yawDegrees'>> = [
  { id: 'front', yawRadians: 0, yawDegrees: 0 },
  { id: 'three-quarter', yawRadians: Math.PI / 4, yawDegrees: 45 },
  { id: 'profile', yawRadians: Math.PI / 2, yawDegrees: 90 },
  { id: 'back-three-quarter', yawRadians: (Math.PI * 3) / 4, yawDegrees: 135 },
  { id: 'back', yawRadians: Math.PI, yawDegrees: 180 },
  { id: 'opposite-profile', yawRadians: -Math.PI / 2, yawDegrees: -90 },
];

export function createBillboardViews(basePath: string, measurements: BillboardMeasurementSet): BillboardView[] {
  return BILLBOARD_DIRECTIONS.map((direction) => ({
    ...direction,
    ...measurements[direction.id],
    src: `${basePath}/${direction.id}.png`,
  }));
}

export function createBillboardAssets(basePath: string, measurements: BillboardMeasurementSet): BillboardAssets {
  return {
    views: createBillboardViews(`${basePath}/directions`, measurements),
    reactions: {
      positive: { id: 'positive', ...measurements.positive, src: `${basePath}/reactions/positive.png` },
      negative: { id: 'negative', ...measurements.negative, src: `${basePath}/reactions/negative.png` },
    },
  };
}

export interface BillboardGeometry {
  scale: number;
  planeWidth: number;
  planeHeight: number;
  meshCenterY: number;
  opaqueBodyHeight: number;
  opaqueBodyBottomY: number;
}

export function resolveBillboardGeometry(
  characterHeightMeters: number,
  asset: BillboardAssetMeasurement,
  sourcePlaneHeight = 2.04,
): BillboardGeometry {
  const opaqueBodyFraction = (
    asset.opaqueBodyBottomPx - asset.opaqueBodyTopPx + 1
  ) / asset.canvasHeight;
  const scale = characterHeightMeters / (sourcePlaneHeight * opaqueBodyFraction);
  const planeHeight = sourcePlaneHeight * scale;
  const feetAnchorY = (asset.opaqueBodyBottomPx + 1) / asset.canvasHeight;

  return {
    scale,
    planeWidth: planeHeight * (asset.canvasWidth / asset.canvasHeight),
    planeHeight,
    meshCenterY: planeHeight * (feetAnchorY - 0.5),
    opaqueBodyHeight: planeHeight * opaqueBodyFraction,
    opaqueBodyBottomY: planeHeight * (feetAnchorY - 0.5)
      + planeHeight * (0.5 - feetAnchorY),
  };
}

export const BILLBOARD_HYSTERESIS_RADIANS = (8 * Math.PI) / 180;

export function wrapRadians(value: number) {
  let wrapped = value % (Math.PI * 2);
  if (wrapped > Math.PI) wrapped -= Math.PI * 2;
  if (wrapped <= -Math.PI) wrapped += Math.PI * 2;
  return wrapped;
}

export function cameraRelativeYaw(
  cameraPosition: [number, number, number],
  npcPosition: [number, number, number],
  facingY: number,
) {
  const cameraYaw = Math.atan2(
    cameraPosition[0] - npcPosition[0],
    cameraPosition[2] - npcPosition[2],
  );
  // Authored NPC poses face along local -Z. Measure from that forward axis and
  // keep the sprite sheet's clockwise view ordering in screen space.
  return wrapRadians(facingY + Math.PI - cameraYaw);
}

function angularDistance(a: number, b: number) {
  return Math.abs(wrapRadians(a - b));
}

export function selectBillboardView(
  relativeYawRadians: number,
  views: BillboardView[],
  previousViewId: string | null = null,
  hysteresisRadians = BILLBOARD_HYSTERESIS_RADIANS,
): BillboardSelection {
  if (views.length === 0) throw new Error('At least one billboard view is required');

  const nearest = views.reduce((best, candidate) => (
    angularDistance(relativeYawRadians, candidate.yawRadians)
      < angularDistance(relativeYawRadians, best.yawRadians)
      ? candidate
      : best
  ));
  const previous = views.find((view) => view.id === previousViewId);
  const view = previous && previous.id !== nearest.id
    && angularDistance(relativeYawRadians, previous.yawRadians)
      <= angularDistance(relativeYawRadians, nearest.yawRadians) + hysteresisRadians
    ? previous
    : nearest;

  return {
    view,
    relativeYawRadians: wrapRadians(relativeYawRadians),
    switched: view.id !== previousViewId,
  };
}

export interface BillboardSelection {
  view: BillboardView;
  relativeYawRadians: number;
  switched: boolean;
}
